import type { Enums, Tables } from '../../types/database.types';
import { newLeadStage } from './stageKeys';

/**
 * Outbound Tracker (unification, Stage 2): sales shapes and pure lenses.
 * Access is the database's job (the sales_members gate); the consent rule
 * (Inbound is permanent) lives in a trigger — the interface only mirrors it.
 */

export type Lead = Tables<'leads'>;
export type LeadStage = Tables<'lead_stages'>;
export type Company = Tables<'companies'>;
export type LeadSource = Enums<'lead_source'>;

export const SOURCE_LABELS: Record<LeadSource, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

export const SOURCE_TONES: Record<LeadSource, 'success' | 'brand'> = {
  inbound: 'success',
  outbound: 'brand',
};

export function leadName(lead: Lead): string {
  const name = `${lead.first_name} ${lead.last_name}`.trim();
  return name === '' ? lead.email || '(no name)' : name;
}

/** Pipeline lens: one column per stage in position order. */
export function leadsByStage(leads: Lead[], stages: LeadStage[]): Map<string, Lead[]> {
  const columns = new Map<string, Lead[]>();
  for (const stage of [...stages].sort((a, b) => a.position - b.position)) {
    columns.set(stage.id, []);
  }
  for (const lead of leads) {
    columns.get(lead.stage_id)?.push(lead);
  }
  return columns;
}

/** Search lens: case-insensitive match on name, email, company and title. */
export function searchLeads(leads: Lead[], query: string): Lead[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return leads;
  }
  return leads.filter((lead) =>
    [lead.first_name, lead.last_name, lead.email, lead.company_name, lead.title]
      .join(' ')
      .toLowerCase()
      .includes(needle),
  );
}

export function salesErrorMessage(message: string): string {
  if (message.includes('lead_consent_is_permanent')) {
    // Both directions, not just Inbound: since 20261203000100 an answered Lead
    // Source cannot be changed by a plain update either way. Naming only
    // Inbound would misdescribe the refusal on an outbound lead.
    return 'This lead’s Lead Source is already recorded and cannot be changed here. A TAP admin can correct it with a reason.';
  }
  if (message.includes('leads_email_unique') || message.includes('duplicate key')) {
    return 'A lead with this email already exists.';
  }
  return 'That change could not be saved. Try again.';
}

export type LeadList = Tables<'lead_lists'>;
export type Membership = Tables<'lead_list_memberships'>;
// Older fixtures and cached inputs can lack the new metadata. Production
// catalog decoding requires it; versioned saves never invent a revision.
type CopyMessageMetadata =
  'is_one_off' | 'review_state' | 'reviewed_by' | 'reviewed_at' | 'message_revision';
export type CopySet = Omit<Tables<'copy_sets'>, CopyMessageMetadata> &
  Partial<Pick<Tables<'copy_sets'>, CopyMessageMetadata>> & { message_used?: boolean };
export type SequenceStatus = Enums<'sequence_status'>;

export const SEQ_LABELS: Record<SequenceStatus, string> = {
  active: 'Active',
  bounced: 'Bounced',
  opted_out: 'Opted Out',
  replied: 'Replied',
};

export const SEQ_TONES: Record<SequenceStatus, 'brand' | 'danger' | 'neutral' | 'success'> = {
  active: 'brand',
  bounced: 'danger',
  opted_out: 'neutral',
  replied: 'success',
};

export const TOUCHES = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type Touch = (typeof TOUCHES)[number];

/**
 * The sequence was four emails long from the original tracker until drop 139.
 * Nothing ever shows fewer boxes than this, so lengthening the sequence cannot
 * take a box away from a campaign that was already running on four.
 */
export const LEGACY_TOUCH_COUNT = 4;

/** The chosen touch's email from a copy set. Same coalesce rule as touchOf. */
export function copyEmail(set: CopySet, touch: Touch): { subject: string; body: string } {
  return { subject: set[`e${touch}_subject`] ?? '', body: set[`e${touch}_body`] ?? '' };
}

/**
 * Membership touch fields for a touch number.
 *
 * The `?? null` and `?? ''` are load-bearing, not defensive noise. A row that
 * came back from PostgREST without the column carries `undefined`, and every
 * caller here tests `sentOn !== null`, which `undefined` passes. Without the
 * coalesce, one missing column in a select list makes `lastSentTouch` report
 * touch 8 as sent on every membership in the Hub. leadColumns.test.ts is what
 * stops the select list going short; this is the second floor.
 */
export function touchOf(
  membership: Membership,
  touch: Touch,
): { status: string; sentOn: string | null } {
  return {
    status: membership[`touch${touch}_status`] ?? '',
    sentOn: membership[`touch${touch}_sent_on`] ?? null,
  };
}

/**
 * How long a campaign is: the highest step whose subject or body has anything
 * in it. A gap does not end the sequence. A set with 1, 2 and 5 written is five
 * steps long and step 3 reads as an email nobody has drafted yet, because the
 * alternative is silently dropping step 5 from the grid.
 */
export function copySetLength(set: CopySet): number {
  let length = 0;
  for (const touch of TOUCHES) {
    const email = copyEmail(set, touch);
    if (email.subject.trim() !== '' || email.body.trim() !== '') {
      length = touch;
    }
  }
  return length;
}

/** The steps a copy set covers, in order. Never fewer than the legacy four. */
export function copySetTouches(set: CopySet | null): Touch[] {
  const length = set === null ? LEGACY_TOUCH_COUNT : copySetLength(set);
  return TOUCHES.filter((touch) => touch <= Math.max(length, LEGACY_TOUCH_COUNT));
}

/**
 * The boxes one membership shows. The campaign sets the length, but a touch
 * that already carries a date or a status is always shown even when the copy
 * set no longer reaches it. Shortening a copy set must not hide a send that
 * happened.
 */
export function membershipTouches(membership: Membership, set: CopySet | null): Touch[] {
  let last = set === null ? LEGACY_TOUCH_COUNT : Math.max(copySetLength(set), LEGACY_TOUCH_COUNT);
  for (const touch of TOUCHES) {
    const field = touchOf(membership, touch);
    if (field.sentOn !== null || field.status !== '') {
      last = Math.max(last, touch);
    }
  }
  return TOUCHES.filter((touch) => touch <= last);
}

/** Copy sets by id, so the grid does not scan the list once per cell. */
export function copySetIndex(sets: CopySet[]): Map<string, CopySet> {
  return new Map(sets.map((set) => [set.id, set]));
}

/** The copy set a membership runs on, or null when it has none. */
export function setOfMembership(
  membership: Membership,
  sets: Map<string, CopySet>,
): CopySet | null {
  return membership.copy_set_id === null ? null : (sets.get(membership.copy_set_id) ?? null);
}

/**
 * How many touch columns the Lists grid draws. A table has one column count
 * for every row, so it is the longest campaign on screen. A row whose own
 * campaign is shorter shows nothing to tick in the extra columns, rather than
 * an empty box that would write a touch that campaign does not have.
 */
export function gridTouches(memberships: Membership[], sets: Map<string, CopySet>): Touch[] {
  let last = LEGACY_TOUCH_COUNT;
  for (const membership of memberships) {
    const highest = membershipTouches(membership, setOfMembership(membership, sets)).at(-1);
    if (highest !== undefined) {
      last = Math.max(last, highest);
    }
  }
  return TOUCHES.filter((touch) => touch <= last);
}

export type SalesTask = Tables<'sales_tasks'>;
export type LinkedInDay = Tables<'linkedin_activity'>;

export type TaskFilter = 'all' | 'overdue' | 'today' | 'week' | 'nodate';

export const TASK_FILTER_LABELS: Record<TaskFilter, string> = {
  all: 'All',
  overdue: 'Overdue',
  today: 'Due today',
  week: 'This week',
  nodate: 'No date',
};

/** Open tasks matching a filter; `today` is an ISO date (YYYY-MM-DD). */
export function filterTasks(tasks: SalesTask[], filter: TaskFilter, today: string): SalesTask[] {
  const open = tasks.filter((task) => task.status === 'open');
  if (filter === 'all') {
    return open;
  }
  if (filter === 'nodate') {
    return open.filter((task) => task.due_on === null);
  }
  if (filter === 'overdue') {
    return open.filter((task) => task.due_on !== null && task.due_on < today);
  }
  if (filter === 'today') {
    return open.filter((task) => task.due_on === today);
  }
  const weekEnd = new Date(`${today}T00:00:00Z`);
  weekEnd.setUTCDate(weekEnd.getUTCDate() + 7);
  const weekEndIso = weekEnd.toISOString().slice(0, 10);
  return open.filter(
    (task) => task.due_on !== null && task.due_on >= today && task.due_on <= weekEndIso,
  );
}

export interface LinkedInStats {
  connectionsSent: number;
  newConnections: number;
  acceptanceRate: number; // 0-100, rounded
  dmsSent: number;
  messagesReceived: number;
  avgConnectionsPerDay: number; // over days with entries, rounded
}

export function linkedInStats(days: LinkedInDay[]): LinkedInStats {
  const connectionsSent = days.reduce((sum, day) => sum + day.connections_sent, 0);
  const newConnections = days.reduce((sum, day) => sum + day.new_connections, 0);
  return {
    connectionsSent,
    newConnections,
    acceptanceRate:
      connectionsSent === 0 ? 0 : Math.round((newConnections / connectionsSent) * 1000) / 10,
    dmsSent: days.reduce((sum, day) => sum + day.dms_sent, 0),
    messagesReceived: days.reduce((sum, day) => sum + day.messages_received, 0),
    avgConnectionsPerDay: days.length === 0 ? 0 : Math.round(connectionsSent / days.length),
  };
}

/**
 * Touch cadence, as gaps: each touch is due this many business days after
 * the touch before it went out (or, when that one has not gone out yet, after
 * its own due date). Touch 2 +5 after Touch 1, Touch 3 +5 after Touch 2,
 * Touch 4 +3 after Touch 3. A row sent on schedule lands on the old tracker's
 * dates (+5, +10, +13 from Touch 1); a late send moves every later date with
 * it instead of leaving them red. Marking the sequence Replied stops the
 * remaining touches.
 */
export const TOUCH_GAPS: Record<Exclude<Touch, 1>, number> = {
  2: 5,
  3: 5,
  4: 3,
  // PLACEHOLDER, awaiting Nez. Steps 5 to 8 have never had a spacing. +3
  // business days per step keeps the late-sequence gap the same as touch 3 to 4.
  // Change these to whatever she actually runs before anyone trusts the dates.
  5: 3,
  6: 3,
  7: 3,
  8: 3,
};

/** Adds n business days (Mon–Fri) to an ISO date (YYYY-MM-DD). */
export function addBusinessDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  let remaining = days;
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const day = date.getUTCDay();
    if (day !== 0 && day !== 6) {
      remaining -= 1;
    }
  }
  return date.toISOString().slice(0, 10);
}

/**
 * When a touch is due, per the cadence. Null when it does not apply: touch 1,
 * touch already sent, no Touch 1 date yet, or the sequence is not active.
 *
 * Walks the chain from Touch 1: each step takes the real sent date when there
 * is one, else a date someone set by hand, else the previous date plus that
 * step's gap. So the anchor is the previous touch, never Touch 1 alone, and a
 * hand-set date moves every automatic date after it but is never moved itself
 * (Ced, kyu-20260929-editable-touch-dates).
 */
export function touchDueOn(
  membership: Membership,
  touch: Touch,
  holdUntil?: string | null,
): string | null {
  if (touch === 1 || membership.seq_status !== 'active' || membership.touch1_sent_on === null) {
    return null;
  }
  if (touchOf(membership, touch).sentOn !== null) {
    return null;
  }
  let dueOn = membership.touch1_sent_on;
  for (const step of TOUCHES) {
    if (step === 1 || step > touch) {
      continue;
    }
    dueOn =
      touchOf(membership, step).sentOn ??
      handSetDueOn(membership, step) ??
      addBusinessDays(dueOn, TOUCH_GAPS[step]);
  }
  return holdUntil !== null && holdUntil !== undefined && holdUntil > dueOn ? holdUntil : dueOn;
}

/**
 * The due date someone set by hand for a touch, or null when it is automatic.
 * `?? null` for the same reason as touchOf: a row without the column must read
 * as automatic, not as a date.
 */
export function handSetDueOn(membership: Membership, touch: Exclude<Touch, 1>): string | null {
  return membership[`touch${touch}_due_on`] ?? null;
}

/** The one column Save or "Back to automatic" writes. Null clears it. */
export function dueDateChanges(
  touch: Exclude<Touch, 1>,
  on: string | null,
): Partial<Record<`touch${Exclude<Touch, 1>}_due_on`, string | null>> {
  return { [`touch${touch}_due_on`]: on };
}

/**
 * Why a hand-set date is refused, or null when it is fine. The only rule: not
 * before the day the previous touch actually went out. Weekends are allowed;
 * the person chose the day.
 */
export function dueDateRefusal(
  membership: Membership,
  touch: Exclude<Touch, 1>,
  on: string,
): string | null {
  const previous = (touch - 1) as Touch;
  const previousSent = touchOf(membership, previous).sentOn;
  if (previousSent !== null && on < previousSent) {
    return `Pick a date on or after ${previousSent}, when Touch ${previous} went out`;
  }
  return null;
}

/** The highest touch already sent, or null when none are. */
export function lastSentTouch(membership: Membership): Touch | null {
  for (const touch of [...TOUCHES].reverse()) {
    if (touchOf(membership, touch).sentOn !== null) {
      return touch;
    }
  }
  return null;
}

/** Memberships whose sequence has started (Touch 1 sent). */
export function touchedCount(memberships: Membership[]): number {
  return memberships.filter((membership) => membership.touch1_sent_on !== null).length;
}

export interface ListSendStats {
  listId: string;
  sends: number[]; // per touch, index 0 = Touch 1
  total: number;
  replies: number;
}

/** Sends and replies per list, in the memberships' list order. */
export function sendsPerList(memberships: Membership[]): ListSendStats[] {
  const byList = new Map<string, ListSendStats>();
  for (const membership of memberships) {
    const entry = byList.get(membership.list_id) ?? {
      listId: membership.list_id,
      sends: TOUCHES.map(() => 0),
      total: 0,
      replies: 0,
    };
    TOUCHES.forEach((touch, index) => {
      if (touchOf(membership, touch).sentOn !== null) {
        entry.sends[index] = (entry.sends[index] ?? 0) + 1;
        entry.total += 1;
      }
    });
    if (membership.reply_touch !== null) {
      entry.replies += 1;
    }
    byList.set(membership.list_id, entry);
  }
  return [...byList.values()];
}

/**
 * The Cold stage, by name when it exists and by lowest position otherwise.
 * Cold is a holding pen, not a pipeline state: its hundreds of untouched
 * leads leave the Kanban entirely (redesign spec) and live in the Backlog
 * drawer instead.
 */
export function coldStageOf(stages: LeadStage[]): LeadStage | null {
  // Was: match the name 'cold', else fall back to whichever status sorts
  // first. Both halves were wrong after the rename — the name no longer
  // exists, and the fallback meant a mis-seeded vocabulary silently filed
  // every untouched lead under an arbitrary status without erroring.
  return newLeadStage(stages);
}

/** Each lead's most recent sent touch date across every membership. */
export function lastTouchByLead(memberships: Membership[]): Map<string, string> {
  const latest = new Map<string, string>();
  for (const membership of memberships) {
    for (const touch of TOUCHES) {
      const sentOn = touchOf(membership, touch).sentOn;
      if (sentOn === null) {
        continue;
      }
      const known = latest.get(membership.lead_id);
      if (known === undefined || sentOn > known) {
        latest.set(membership.lead_id, sentOn);
      }
    }
  }
  return latest;
}

/** Whole days from an ISO date to today; 0 = today, negative never happens in practice. */
export function daysSince(dateIso: string, todayIso: string): number {
  const then = new Date(`${dateIso}T00:00:00Z`).getTime();
  const now = new Date(`${todayIso}T00:00:00Z`).getTime();
  return Math.round((now - then) / 86_400_000);
}

/**
 * Deal value rides on the company (companies.deal_value, set in the Company
 * panel); a lead's card shows its company's number. Matching mirrors
 * companyLeadsOf: the linked id wins, an unlinked lead matches by name.
 */
export function dealValueByLead(leads: Lead[], companies: Company[]): Map<string, number> {
  const byId = new Map(companies.map((company) => [company.id, company]));
  const byName = new Map(companies.map((company) => [company.name.trim().toLowerCase(), company]));
  const values = new Map<string, number>();
  for (const lead of leads) {
    const company =
      lead.company_id !== null
        ? byId.get(lead.company_id)
        : byName.get(lead.company_name.trim().toLowerCase());
    if (company !== undefined && company.deal_value !== null) {
      values.set(lead.id, company.deal_value);
    }
  }
  return values;
}

export type SourceFilter = 'all' | 'outbound' | 'inbound' | 'not-set';

export const SOURCE_FILTER_LABELS: Record<SourceFilter, string> = {
  all: 'All',
  outbound: 'Outbound',
  inbound: 'Inbound',
  'not-set': 'Not set',
};

/**
 * Which side of the business a lead came from — three answers, not two.
 *
 * ── What this replaces, and why ────────────────────────────────────────────
 *
 * Every reader of `leads.source` used to fold null into outbound. The rule was
 * inherited from the spreadsheet and written down at the time: "counting the
 * way the Sheet does: everything that is not explicitly Inbound is Outbound
 * (443 blank-source rows are outbound)."
 *
 * Measured on the live app, 25 Aug: 787 leads, 21 inbound, 98 outbound, and
 * 668 with nothing set. The Dashboard was reporting 766 outbound. The data was
 * never wrong; the reading of it turned "we do not know" into a confident
 * answer, on the card Alex looks at.
 *
 * Kyu: "Do not let 'Not set' get counted as Outbound."
 *
 * One function so the three readers cannot drift apart again — the KPI card,
 * the Reports scope and this filter each had their own copy of the rule, which
 * is how one of them could be fixed and the others left lying.
 */
export type LeadChannel = 'inbound' | 'outbound' | 'not-set';

export function leadChannel(lead: Pick<Lead, 'source'>): LeadChannel {
  if (lead.source === 'inbound') return 'inbound';
  if (lead.source === 'outbound') return 'outbound';
  return 'not-set';
}

export interface ChannelCounts {
  inbound: number;
  outbound: number;
  notSet: number;
}

export function channelCounts(leads: Lead[]): ChannelCounts {
  const counts: ChannelCounts = { inbound: 0, outbound: 0, notSet: 0 };
  for (const lead of leads) {
    const channel = leadChannel(lead);
    if (channel === 'inbound') counts.inbound += 1;
    else if (channel === 'outbound') counts.outbound += 1;
    else counts.notSet += 1;
  }
  return counts;
}

export function filterBySource(leads: Lead[], filter: SourceFilter): Lead[] {
  if (filter === 'all') {
    return leads;
  }
  return leads.filter((lead) => leadChannel(lead) === filter);
}

/** A company's leads: linked by id, or by name for never-linked rows. */
export function companyLeadsOf(company: Company, leads: Lead[]): Lead[] {
  const name = company.name.trim().toLowerCase();
  return leads.filter(
    (lead) =>
      lead.company_id === company.id ||
      (lead.company_id === null && lead.company_name.trim().toLowerCase() === name),
  );
}

/**
 * The stage a company reads as: the manual override when set, otherwise the
 * furthest stage (highest position) among its leads. Null with no signal.
 */
export function companyStage(
  company: Company,
  leads: Lead[],
  stages: LeadStage[],
): { stage: LeadStage; overridden: boolean } | null {
  if (company.stage_override_id !== null) {
    const stage = stages.find((s) => s.id === company.stage_override_id);
    if (stage !== undefined) {
      return { stage, overridden: true };
    }
  }
  let best: LeadStage | null = null;
  for (const lead of companyLeadsOf(company, leads)) {
    const stage = stages.find((s) => s.id === lead.stage_id);
    if (stage !== undefined && (best === null || stage.position > best.position)) {
      best = stage;
    }
  }
  return best === null ? null : { stage: best, overridden: false };
}

/** Duplicate lens for companies: groups of 2+ sharing a normalized name. */
export function duplicateCompanyGroups(companies: Company[]): Company[][] {
  const byName = new Map<string, Company[]>();
  for (const company of companies) {
    const key = company.name.trim().toLowerCase();
    if (key === '') {
      continue;
    }
    byName.set(key, [...(byName.get(key) ?? []), company]);
  }
  return [...byName.values()].filter((group) => group.length > 1);
}

/** One CSV cell, quoted whenever the content could break the format. */
export function csvEscape(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Rows to CSV text (CRLF, per the format's convention). */
export function toCsv(rows: string[][]): string {
  return rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
}

/**
 * Replies over touched, for ONE list's memberships.
 *
 * A cycle helper, not a lead-truth helper: the Lists index summarises each
 * cycle from its own membership rows, which is the honest source for a cycle.
 * The Dashboard's all-up reply rate reads the timeline in activityStats.ts.
 */
export function replyRateOfTouched(memberships: Membership[]): {
  replies: number;
  touched: number;
  label: string;
} {
  const touched = memberships.filter((m) => m.touch1_sent_on !== null);
  const replies = touched.filter((m) => m.reply_touch !== null || m.reply_date !== null).length;
  const label =
    touched.length === 0 ? '—' : `${String(Math.round((replies / touched.length) * 100))}%`;
  return { replies, touched: touched.length, label };
}

/** The latest sent touch across a set of memberships, for the cycle tables. */
export function lastTouchAcross(
  memberships: Membership[],
): { touch: Touch; sentOn: string } | null {
  let best: { touch: Touch; sentOn: string } | null = null;
  for (const membership of memberships) {
    for (const touch of TOUCHES) {
      const sentOn = touchOf(membership, touch).sentOn;
      if (sentOn !== null && (best === null || sentOn > best.sentOn)) {
        best = { touch, sentOn };
      }
    }
  }
  return best;
}

// The membership-based stat helpers that lived here — todayStats,
// dailyActivity, conversionFunnel, replyRateOfTouched, rangeReport,
// rangeDaily, lastTouchAcross, splitBacklog — went in batch 4. They counted
// touches off T1–T4 checkboxes, so a lead emailed outside any list read as
// never touched; their replacements read the activity timeline and live in
// activityStats.ts. RangeReport stays: the Slack and email summaries consume
// it, and the new builder fills the same shape.
export interface RangeReport {
  from: string;
  to: string;
  newLeads: number;
  inbound: number;
  touchesSent: number;
  replies: number;
  replyRate: { replies: number; touched: number; label: string };
  byStage: { name: string; count: number }[];
}

/** The report as a Slack-ready message, matching the old tracker's habit. */
export function slackSummary(report: RangeReport): string {
  const pipeline = report.byStage
    .filter((row) => row.count > 0)
    .map((row) => `${row.name} ${row.count}`)
    .join(' · ');
  return [
    `📊 *Sales Report ${report.from} → ${report.to}*`,
    `New leads: ${report.newLeads} (${report.inbound} inbound)`,
    `Touches sent: ${report.touchesSent}`,
    `Replies: ${report.replies} (all-time reply rate ${report.replyRate.label} of ${report.replyRate.touched} touched)`,
    `Pipeline now: ${pipeline === '' ? 'empty' : pipeline}`,
  ].join('\n');
}

/** The same numbers phrased for an email body: plain sentences, no markup. */
export function emailSummary(report: RangeReport): string {
  const pipeline = report.byStage
    .filter((row) => row.count > 0)
    .map((row) => `${row.name} (${row.count})`)
    .join(', ');
  return [
    `Sales report for ${report.from} to ${report.to}:`,
    '',
    `We added ${report.newLeads} new leads (${report.inbound} inbound) and sent ${report.touchesSent} touches.`,
    `${report.replies} replies came in; the all-time reply rate stands at ${report.replyRate.label} of ${report.replyRate.touched} touched leads.`,
    `The pipeline currently holds: ${pipeline === '' ? 'no active leads' : pipeline}.`,
  ].join('\n');
}

/** The same numbers as Notion-ready markdown: heading plus bullets. */
export function notionSummary(report: RangeReport): string {
  const pipeline = report.byStage
    .filter((row) => row.count > 0)
    .map((row) => `${row.name} ${row.count}`)
    .join(' · ');
  return [
    `## Sales Report ${report.from} → ${report.to}`,
    `- **New leads:** ${report.newLeads} (${report.inbound} inbound)`,
    `- **Touches sent:** ${report.touchesSent}`,
    `- **Replies:** ${report.replies} (all-time reply rate ${report.replyRate.label} of ${report.replyRate.touched} touched)`,
    `- **Pipeline now:** ${pipeline === '' ? 'empty' : pipeline}`,
  ].join('\n');
}

/** Every lead as CSV, with stage, lists and last touch resolved. */
export function leadsCsv(
  leads: Lead[],
  stages: LeadStage[],
  memberships: Membership[],
  lists: LeadList[],
): string {
  const byLead = new Map<string, Membership[]>();
  for (const membership of memberships) {
    byLead.set(membership.lead_id, [...(byLead.get(membership.lead_id) ?? []), membership]);
  }
  const rows: string[][] = [
    [
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Company',
      'Title',
      'Stage',
      'Source',
      'Lists',
      'Last Touch',
      'Last Touch Date',
      'Created',
    ],
  ];
  for (const lead of leads) {
    const mine = byLead.get(lead.id) ?? [];
    const last = lastTouchAcross(mine);
    rows.push([
      lead.first_name,
      lead.last_name,
      lead.email,
      lead.phone,
      lead.company_name,
      lead.title,
      String(stages.find((s) => s.id === lead.stage_id)?.name ?? ''),
      lead.source === null ? '' : SOURCE_LABELS[lead.source],
      mine
        .map((m) => String(lists.find((l) => l.id === m.list_id)?.name ?? ''))
        .filter((name) => name !== '')
        .join('; '),
      last === null ? '' : `T${last.touch}`,
      last === null ? '' : last.sentOn,
      lead.created_at.slice(0, 10),
    ]);
  }
  return toCsv(rows);
}

/** The range report as CSV: summary block, then the per-day table. */
export function reportCsv(
  report: RangeReport,
  daily: { date: string; newLeads: number; touchesSent: number; replies: number }[],
): string {
  return toCsv([
    ['Sales Report', `${report.from} to ${report.to}`],
    ['New Leads', String(report.newLeads)],
    ['Inbound', String(report.inbound)],
    ['Touches Sent', String(report.touchesSent)],
    ['Replies', String(report.replies)],
    ['Reply Rate (all-time)', report.replyRate.label],
    [],
    ['Date', 'New Leads', 'Touches Sent', 'Replies'],
    ...daily.map((row) => [
      row.date,
      String(row.newLeads),
      String(row.touchesSent),
      String(row.replies),
    ]),
  ]);
}

/**
 * Reply performance per copy set, counted exactly like the dashboard's Copy
 * Performance table: one membership = one lead served, replied when any
 * touch drew a reply.
 */
export function copySetPerformance(
  memberships: Membership[],
): Map<string, { leads: number; replies: number }> {
  const bySet = new Map<string, { leads: number; replies: number }>();
  for (const membership of memberships) {
    if (membership.copy_set_id === null) {
      continue;
    }
    const entry = bySet.get(membership.copy_set_id) ?? { leads: 0, replies: 0 };
    entry.leads += 1;
    if (membership.reply_touch !== null) {
      entry.replies += 1;
    }
    bySet.set(membership.copy_set_id, entry);
  }
  return bySet;
}

/**
 * The best-converting set, for the spec's Top Performer badge. It only means
 * something once a set has real traffic behind it, so a set needs at least
 * MIN_TOP_PERFORMER_LEADS assigned leads and one reply to qualify — otherwise
 * a single lucky reply on a two-lead set would crown itself.
 */
export const MIN_TOP_PERFORMER_LEADS = 10;

export function topPerformingCopySetId(
  performance: Map<string, { leads: number; replies: number }>,
): string | null {
  let bestId: string | null = null;
  let bestRate = 0;
  for (const [id, entry] of performance) {
    if (entry.leads < MIN_TOP_PERFORMER_LEADS || entry.replies === 0) {
      continue;
    }
    const rate = entry.replies / entry.leads;
    if (rate > bestRate) {
      bestRate = rate;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * Per-list headline numbers for the Lists index (spec §Lists: lead count,
 * reply rate, last touch).
 *
 * Reply rate here is replies over touched WITHIN THE CYCLE, which is the
 * honest number for a cycle. It is deliberately not the Dashboard's figure:
 * that one reads the activity timeline, because a lead emailed from Gmail and
 * never put on a list is invisible to memberships. This comment used to claim
 * the two shared a definition. They stopped sharing one when the Dashboard was
 * fixed, and nothing said so until a source guard was widened.
 */
export interface ListIndexStat {
  leads: number;
  replyLabel: string;
  lastTouch: string | null;
}

export function listIndexStats(memberships: Membership[]): Map<string, ListIndexStat> {
  const byList = new Map<string, Membership[]>();
  for (const membership of memberships) {
    const group = byList.get(membership.list_id) ?? [];
    group.push(membership);
    byList.set(membership.list_id, group);
  }

  const stats = new Map<string, ListIndexStat>();
  for (const [listId, group] of byList) {
    stats.set(listId, {
      leads: group.length,
      replyLabel: replyRateOfTouched(group).label,
      lastTouch: lastTouchAcross(group)?.sentOn ?? null,
    });
  }
  return stats;
}

/** Comma-separated notes as clean tags; empty pieces drop out. */
export function splitNoteTags(notes: string): string[] {
  return notes
    .split(',')
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '');
}

/** The insertable personalization tokens, in toolbar order. */
export const COPY_TOKENS = ['{{first_name}}', '{{last_name}}', '{{company}}', '{{title}}'] as const;

/**
 * Personalization tokens, filled for one lead.
 *
 * Accepts every spelling the copy library has used: `{{first_name}}` (what
 * the token buttons insert — COPY_TOKENS), `{{FirstName}}` and `{{first name}}`
 * (older copy), any case, any spacing. The underscore form is the one that
 * matters and the one this used to miss: `\s*` does not match `_`, so
 * `{{first_name}}` — the form on every current copy set — went out unfilled,
 * and send-as-mailbox refused it with "a placeholder nobody filled in".
 * Hanneza hit exactly that on her first test send, 3 September.
 */
export function replaceTokens(text: string, lead: Lead): string {
  return text
    .replace(/\{\{\s*first[\s_]*name\s*\}\}/gi, lead.first_name)
    .replace(/\{\{\s*last[\s_]*name\s*\}\}/gi, lead.last_name)
    .replace(/\{\{\s*company\s*\}\}/gi, lead.company_name)
    .replace(/\{\{\s*title\s*\}\}/gi, lead.title);
}

// ── shared csv: keep identical with supabase/functions/import-prospects ─────
/** A raw CSV cell grid (quoted cells, embedded newlines supported). */
export function parseCsvGrid(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch !== '\r') {
      cell += ch;
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((value) => value.trim() !== ''));
}
// ── end shared csv ──────────────────────────────────────────────────────────

export interface ImportedLead {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  title: string;
}

const HEADER_ALIASES: Record<string, keyof ImportedLead> = {
  'first name': 'firstName',
  firstname: 'firstName',
  first: 'firstName',
  'last name': 'lastName',
  lastname: 'lastName',
  last: 'lastName',
  email: 'email',
  'email address': 'email',
  phone: 'phone',
  'phone number': 'phone',
  company: 'companyName',
  'company name': 'companyName',
  organization: 'companyName',
  title: 'title',
  'job title': 'title',
  role: 'title',
};

/**
 * Import lens: reads a pasted CSV, maps common headers, and splits rows
 * into new leads vs duplicates of existing emails (case-insensitive).
 */
export function parseLeadImport(
  text: string,
  existing: Lead[],
): { newLeads: ImportedLead[]; duplicates: number; skipped: number; error: string } {
  const grid = parseCsvGrid(text);
  if (grid.length < 2) {
    return {
      newLeads: [],
      duplicates: 0,
      skipped: 0,
      error: 'Paste a CSV with a header row and at least one lead.',
    };
  }
  const header = grid[0] ?? [];
  const mapping = header.map((cell) => HEADER_ALIASES[cell.trim().toLowerCase()] ?? null);
  if (!mapping.includes('email') && !mapping.includes('firstName')) {
    return {
      newLeads: [],
      duplicates: 0,
      skipped: 0,
      error: 'No recognizable columns. Include at least Email or First Name.',
    };
  }
  const known = new Set(
    existing.map((lead) => lead.email.toLowerCase()).filter((email) => email !== ''),
  );
  const seen = new Set<string>();
  const newLeads: ImportedLead[] = [];
  let duplicates = 0;
  let skipped = 0;
  for (const cells of grid.slice(1)) {
    const lead: ImportedLead = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      title: '',
    };
    mapping.forEach((key, index) => {
      if (key !== null) {
        lead[key] = (cells[index] ?? '').trim();
      }
    });
    const emailKey = lead.email.toLowerCase();
    if (lead.email === '' && lead.firstName === '' && lead.lastName === '') {
      skipped += 1;
    } else if (emailKey !== '' && (known.has(emailKey) || seen.has(emailKey))) {
      duplicates += 1;
    } else {
      if (emailKey !== '') {
        seen.add(emailKey);
      }
      newLeads.push(lead);
    }
  }
  return { newLeads, duplicates, skipped, error: '' };
}

// `duplicateLeadGroups` lived here and matched on the full name only. It moved
// to leadDuplicates.ts on 1 Sep, when it turned out the duplicates it missed
// were the ones whose names had arrived mangled — see that file for why.

export interface ReportStats {
  total: number;
  byStage: { name: string; count: number }[];
  inbound: number;
  outbound: number;
  bySequence: { status: SequenceStatus; count: number }[];
  touchesSent: number[];
  repliesByTouch: number[];
  copySetReplies: { copySetId: string | null; replies: number; leads: number }[];
}

/** Reports lens: the numbers the exec snapshot and copy report need. */
export function reportStats(
  leads: Lead[],
  memberships: Membership[],
  stages: LeadStage[],
): ReportStats {
  const byStage = [...stages]
    .sort((a, b) => a.position - b.position)
    .map((stage) => ({
      name: String(stage.name),
      count: leads.filter((lead) => lead.stage_id === stage.id).length,
    }));
  const bySequence = (Object.keys(SEQ_LABELS) as SequenceStatus[]).map((status) => ({
    status,
    count: memberships.filter((membership) => membership.seq_status === status).length,
  }));
  const touchesSent = TOUCHES.map(
    (touch) =>
      memberships.filter((membership) => touchOf(membership, touch).sentOn !== null).length,
  );
  const repliesByTouch = TOUCHES.map(
    (touch) => memberships.filter((membership) => membership.reply_touch === touch).length,
  );
  const bySet = new Map<string | null, { replies: number; leads: number }>();
  for (const membership of memberships) {
    const entry = bySet.get(membership.copy_set_id) ?? { replies: 0, leads: 0 };
    entry.leads += 1;
    if (membership.reply_touch !== null) {
      entry.replies += 1;
    }
    bySet.set(membership.copy_set_id, entry);
  }
  return {
    total: leads.length,
    byStage,
    inbound: leads.filter((lead) => lead.source === 'inbound').length,
    outbound: leads.filter((lead) => lead.source === 'outbound').length,
    bySequence,
    touchesSent,
    repliesByTouch,
    copySetReplies: [...bySet.entries()].map(([copySetId, entry]) => ({ copySetId, ...entry })),
  };
}
